/**
 * Shared formatting utilities for dashboard modules.
 *
 * Provides consistent number and date formatting across all dashboard components.
 */

// =============================================================================
// Number Formatters
// =============================================================================

/**
 * Formats market capitalization value into human-readable format.
 * Assumes input is in millions.
 *
 * @param value - Market cap value in millions.
 * @returns Formatted market cap (e.g., "1.50B", "500.00M", "2.30T").
 *
 * @example
 * formatMarketCap(1500000) // "1.50T"
 * formatMarketCap(1500)    // "1.50B"
 * formatMarketCap(500)     // "500.00M"
 * formatMarketCap(0.5)     // "500.00K"
 * formatMarketCap(0)       // "-"
 */
export function formatMarketCap(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === 0) {
    return "-";
  }

  const numValue = typeof value === "string" ? Number.parseFloat(value) : value;

  if (Number.isNaN(numValue) || numValue === 0) {
    return "-";
  }

  if (numValue >= 1_000_000) {
    return `${(numValue / 1_000_000).toFixed(2)}T`;
  }
  if (numValue >= 1_000) {
    return `${(numValue / 1_000).toFixed(2)}B`;
  }
  if (numValue >= 1) {
    return `${numValue.toFixed(2)}M`;
  }
  return `${(numValue * 1_000).toFixed(2)}K`;
}

/**
 * Formats a percentage value with optional sign prefix.
 *
 * @param value - Percentage value (e.g., 5.25 for 5.25%).
 * @param showSign - Whether to show + prefix for positive values.
 * @param decimals - Number of decimal places (default: 2).
 * @returns Formatted percentage string (e.g., "+5.25%", "-2.50%").
 *
 * @example
 * formatPercentage(5.25, true)   // "+5.25%"
 * formatPercentage(-2.5, true)   // "-2.50%"
 * formatPercentage(5.25, false)  // "5.25%"
 */
export function formatPercentage(
  value: number | null | undefined,
  showSign = false,
  decimals = 2,
): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return "-";
  }

  const formatted = value.toFixed(decimals);
  const sign = showSign && value >= 0 ? "+" : "";

  return `${sign}${formatted}%`;
}

/**
 * Formats a number with thousand separators.
 *
 * @param value - Number to format.
 * @param decimals - Number of decimal places (default: 0).
 * @returns Formatted number string (e.g., "1,234,567").
 *
 * @example
 * formatNumber(1234567)     // "1,234,567"
 * formatNumber(1234.56, 2)  // "1,234.56"
 */
export function formatNumber(value: number | null | undefined, decimals = 0): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return "-";
  }

  return value.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

// =============================================================================
// Date/Time Formatters
// =============================================================================

/**
 * Gets a human-readable relative time string from an ISO date.
 *
 * @param isoDate - ISO date string (e.g., "2024-01-15T10:30:00Z").
 * @returns Relative time string (e.g., "just now", "5m ago", "2h ago", "3d ago").
 *
 * @example
 * getRelativeTime("2024-01-15T10:30:00Z") // "2d ago" (if 2 days have passed)
 */
export function getRelativeTime(isoDate: string | null | undefined): string {
  if (!isoDate) {
    return "-";
  }

  const date = new Date(isoDate);

  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (diffMins < 1) {
    return "just now";
  }
  if (diffMins < 60) {
    return `${diffMins}m ago`;
  }
  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }
  if (diffDays < 7) {
    return `${diffDays}d ago`;
  }

  return date.toLocaleDateString();
}

/**
 * Formats a date for display in charts and tables.
 *
 * @param date - Date object or ISO string.
 * @param format - Format type: 'short' (Jan 15), 'medium' (Jan 15, 2024), 'long' (January 15, 2024).
 * @returns Formatted date string.
 *
 * @example
 * formatDate(new Date(), 'short')  // "Jan 15"
 * formatDate(new Date(), 'medium') // "Jan 15, 2024"
 */
export function formatDate(
  date: Date | string | null | undefined,
  format: "short" | "medium" | "long" = "medium",
): string {
  if (!date) {
    return "-";
  }

  const dateObj = typeof date === "string" ? new Date(date) : date;

  if (Number.isNaN(dateObj.getTime())) {
    return "-";
  }

  const optionsMap: Record<typeof format, Intl.DateTimeFormatOptions> = {
    short: { month: "short", day: "numeric" },
    medium: { month: "short", day: "numeric", year: "numeric" },
    long: { month: "long", day: "numeric", year: "numeric" },
  };
  const options = optionsMap[format];

  return dateObj.toLocaleDateString("en-US", options);
}

// =============================================================================
// CSS Class Helpers
// =============================================================================

/**
 * Gets the CSS class for a positive/negative value.
 *
 * @param value - The numeric value to check.
 * @returns CSS class string for the value's sign.
 *
 * @example
 * getValueColorClass(5.25)  // "text-green-600"
 * getValueColorClass(-2.5)  // "text-red-600"
 * getValueColorClass(0)     // "text-gray-600"
 */
export function getValueColorClass(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return "text-gray-600 dark:text-gray-400";
  }
  if (value > 0) {
    return "text-green-600 dark:text-green-400";
  }
  if (value < 0) {
    return "text-red-600 dark:text-red-400";
  }
  return "text-gray-600 dark:text-gray-400";
}

/**
 * Gets the CSS class for a score value (1-10 scale).
 *
 * @param score - Score value (typically 1-10).
 * @returns CSS class string for the score's quality level.
 *
 * @example
 * getScoreColorClass(8.0) // "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400"
 * getScoreColorClass(5.0) // "bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400"
 * getScoreColorClass(3.0) // "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400"
 */
export function getScoreColorClass(score: number | null | undefined): string {
  if (score === null || score === undefined) {
    return "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400";
  }

  if (score >= 7) {
    return "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400";
  }
  if (score >= 5) {
    return "bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400";
  }
  return "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400";
}

// =============================================================================
// Global Export for Backwards Compatibility
// =============================================================================

// Export to window for legacy usage in templates
if (typeof window !== "undefined") {
  window.formatMarketCap = formatMarketCap;
}
